import { supabase } from './supabase';
import { syncManager } from './syncManager';
import { onSignOut } from './signOutReset';

// Shared IO for the single-row-per-user `user_preferences` table. Several small
// preference stores (theme, language, start-day) each own a slice of the row.
// Writes are partial upserts: PostgREST's ON CONFLICT only updates the columns
// present in the payload, so the stores never clobber each other's columns.

const TABLE = 'user_preferences';

export interface UserPreferencesRow {
  user_id: string;
  /** The row's revision: every write stamps it, whichever column it changed (lib/preferenceSync.ts). */
  updated_at?: string | null;
  theme?: string | null;
  primary_color?: string | null;
  language?: string | null;
  direction?: string | null;
  start_day?: string | null;
  faith?: string | null;
  onboarding_completed?: boolean | null;
  body_figure_gender?: string | null;
  body_figure_color?: string | null;
  hydration_target_ml?: number | null;
  hydration_unit?: string | null;
  nutrition_calorie_target?: number | null;
  nutrition_protein_target?: number | null;
  nutrition_carbs_target?: number | null;
  nutrition_fat_target?: number | null;
  /** Nutrition: food with no linked habit may be logged on past days (mig 0290); null = today only. */
  nutrition_allow_past_days?: boolean | null;
  /** Nutrition direction (mig 0291), written whole by nutritionStore.setDirection. */
  nutrition_direction?: unknown | null;
  fitness_direct_xp?: number | null;
  supplement_stack?: unknown | null;
  medication_stack?: unknown | null;
  /** The person's usual day for Supplements/Medications timing (mig 0304) — `{ wake, breakfast, lunch, dinner, bed }`. */
  intake_day_times?: unknown | null;
  sleep_cycle_type?: string | null;
  sleep_target_minutes?: number | null;
  recovery_protocols?: unknown | null;
  grooming_items?: unknown | null;
  health_vitals?: unknown | null;
  occupational_communication_rank?: number | null;
  notifications?: unknown | null;
  /** Newest release the user has read in What's new (mig 0285) — write via mark_whats_new_seen(), never directly. */
  whats_new_seen?: string | null;
  journal_prompts?: unknown | null;
  care_practices?: unknown | null;
  /** Home's domain pages — `{ version: 1, pages }`, written whole by homeWidgetsStore (mig 0289). */
  home_widgets?: unknown | null;
}

// Returns { userId, row } when authed (row is null if none yet), or null when
// logged out.
//
// Concurrent calls share ONE request: four stores (theme, language, start-day,
// onboarding) each fetch this row during the login init storm — without the
// in-flight memo that was 16 identical GETs per page load.
/**
 * ⚠️ `failed: true` when the READ errored. Without it a failed read and "this
 * user has no row yet" were the same `{ row: null }` — and a caller that asks
 * "did they ever save X?" (Growth Foundations' hydration target) would read a
 * network error as "never saved". Optional, so every existing caller is unchanged.
 */
export interface UserPreferencesFetch { userId: string; row: UserPreferencesRow | null; failed?: boolean }

let inflightFetch: Promise<UserPreferencesFetch | null> | null = null;

export function fetchUserPreferences(): Promise<UserPreferencesFetch | null> {
  if (!inflightFetch) {
    // ⚠️ Clears only its own: after a sign-out the next account's read is another promise.
    const fetching: Promise<UserPreferencesFetch | null> = doFetchUserPreferences().finally(() => {
      if (inflightFetch === fetching) inflightFetch = null;
    });
    inflightFetch = fetching;
  }
  return inflightFetch;
}

/* ⚠️⚠️ A SHARED READ IS ONE ACCOUNT'S READ. Every store's load joins the read in flight,
   so a store loading for the account that signs in next would join the last account's
   read — and take its supplements, vitals and targets as its own. After a sign-out (or
   another account signing in) the next caller starts a read of its own. */
onSignOut(() => { inflightFetch = null; });

async function doFetchUserPreferences(): Promise<UserPreferencesFetch | null> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return null;
    const { data, error } = await supabase.from(TABLE).select('*').limit(1);
    if (error) {
      console.error('Failed to load user preferences:', error);
      return { userId: user.id, row: null, failed: true };
    }
    return { userId: user.id, row: (data ?? [])[0] ?? null };
  } catch (error) {
    console.error('Error loading user preferences:', error);
    return null;
  }
}

// Upsert just the provided columns (plus user_id + updated_at).
/**
 * ⭐ Resolves `true` only when the database ACCEPTED the write — `false` when
 * signed out, refused, or offline. Fire-and-forget callers (`void …`) are
 * unchanged; a caller that must not claim "saved" before it is (Growth
 * Foundations' target step) awaits this instead of trusting the local store.
 */
export async function saveUserPreference(
  partial: Partial<Omit<UserPreferencesRow, 'user_id' | 'updated_at'>>
): Promise<boolean> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return false;
    const { error } = await supabase.from(TABLE).upsert(
      { user_id: user.id, ...partial, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
    if (error) {
      console.error('Failed to save user preference:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error saving user preference:', error);
    return false;
  }
}

// ─── one column, compare-and-swap (lib/preferenceSync.ts) ───────────────────
// The token is the shared row's `updated_at` (NOT NULL, default now(), no
// trigger): another store's preference write in between moves it, the swap
// matches nothing, and the caller re-reads and re-merges.

export type PreferenceColumn = Exclude<keyof UserPreferencesRow, 'user_id' | 'updated_at' | 'whats_new_seen'>;

/**
 * One column and the row's `updated_at`, and whose row it is (`user`). `null` =
 * signed out; no row = `{ updatedAt: null, value: null }`.
 */
export async function readPreferenceColumn(
  column: PreferenceColumn
): Promise<{ updatedAt: string | null; value: unknown; user: string } | null> {
  const { user } = await syncManager.safeAuthCheck();
  if (!user) return null;
  const { data, error } = await supabase
    .from(TABLE)
    .select(`updated_at, ${column}`)
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return { updatedAt: null, value: null, user: user.id };
  const row = data as unknown as Record<string, unknown>;
  return { updatedAt: (row.updated_at as string | null) ?? null, value: row[column] ?? null, user: user.id };
}

/**
 * The row's `updated_at` alone — what a stale tab asks to learn whether ANY
 * preference moved since it last held the row (null = no row; a null result =
 * signed out). A few bytes instead of a column.
 *
 * ⭐ Concurrent calls share ONE request: the nine preference-column stores all
 * refresh when the tab comes back (lib/blobFocusRefresh.ts), and they share
 * this one row, so one answer serves them all.
 */
let inflightRevision: Promise<{ updatedAt: string | null } | null> | null = null;

export function readPreferencesRevision(): Promise<{ updatedAt: string | null } | null> {
  if (!inflightRevision) {
    inflightRevision = doReadPreferencesRevision().finally(() => {
      inflightRevision = null;
    });
  }
  return inflightRevision;
}

async function doReadPreferencesRevision(): Promise<{ updatedAt: string | null } | null> {
  const { user } = await syncManager.safeAuthCheck();
  if (!user) return null;
  const { data, error } = await supabase
    .from(TABLE)
    .select('updated_at')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  return { updatedAt: (data as { updated_at?: string | null } | null)?.updated_at ?? null };
}

/**
 * Write one column only if the row is still at `expectedUpdatedAt` (null = no
 * row yet: insert). `{ updatedAt }` = landed, at that revision (an insert
 * answers `true`: landed, revision unknown); false = someone wrote first
 * (re-read and retry); 'skipped' = signed out, or `forUser` (the account the
 * column was read for) is no longer the one signed in. A refused write throws.
 */
export async function writePreferenceColumn(
  column: PreferenceColumn,
  value: unknown,
  expectedUpdatedAt: string | null,
  forUser?: string,
): Promise<boolean | 'skipped' | { updatedAt: string | null }> {
  const { user } = await syncManager.safeAuthCheck();
  if (!user) return 'skipped';
  // ⚠️ Another account signed in since the column was read: never write the last one's value into its row.
  if (forUser !== undefined && user.id !== forUser) return 'skipped';
  const stamp = new Date().toISOString();
  if (expectedUpdatedAt === null) {
    const { error } = await supabase.from(TABLE).insert({ user_id: user.id, [column]: value, updated_at: stamp });
    if (error) {
      // 23505 = the row appeared meanwhile (another store's first preference).
      if ((error as { code?: string }).code === '23505') return false;
      throw error;
    }
    return true;
  }
  const { data, error } = await supabase
    .from(TABLE)
    .update({ [column]: value, updated_at: stamp })
    .eq('user_id', user.id)
    .eq('updated_at', expectedUpdatedAt)
    .select('user_id, updated_at');
  if (error) throw error;
  // The revision the row is now at, so a refresh knows this tab already holds it.
  const hit = (data as { updated_at?: string | null }[] | null)?.[0];
  return hit ? { updatedAt: hit.updated_at ?? null } : false;
}
